import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Button,
  Card,
  DatePicker,
  message,
  Space,
  Statistic,
  Table,
  Typography,
} from "antd";
import { api } from "../../api/api";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import useGetRequest from "../../hooks/useGetRequest";
import { ICostAnalysis, ICostAnalysisListItem } from "../../types/costAnalysis.type";

const { Title } = Typography;

export default function CostAnalysisPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs().startOf("month"));
  const [creating, setCreating] = useState(false);
  const [current, setCurrent] = useState<ICostAnalysis | null>(null);
  const { data: listData, refresh: refreshList } = useGetRequest("/cost-analysis");
  const { downloadFile, loading: downloadLoading } = useDownloadFile();

  useEffect(() => {
    if (id) {
      api
        .get(`/cost-analysis/${id}`)
        .then((res) => setCurrent(res.data))
        .catch(() => message.error("Не удалось загрузить расчёт."));
    } else {
      setCurrent(null);
    }
  }, [id]);

  const handleCreate = async () => {
    if (!selectedMonth) {
      message.warning("Выберите месяц.");
      return;
    }
    setCreating(true);
    try {
      const periodFrom = selectedMonth.startOf("month").startOf("day");
      const periodTo = selectedMonth.endOf("month").endOf("day");
      const res = await api.post("/cost-analysis", {
        periodFrom: periodFrom.toISOString(),
        periodTo: periodTo.toISOString(),
      });
      const created = res.data.costAnalysis;
      setCurrent(created);
      refreshList();
      navigate(`/cost-analysis/${created._id}`, { replace: true });
      message.success("Расчёт себестоимости создан.");
    } catch (err: any) {
      const msg = err.response?.data?.message || "Ошибка при создании расчёта.";
      message.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!current?._id) return;
    const fromStr = dayjs(current.periodFrom).format("YYYY-MM-DD");
    const toStr = dayjs(current.periodTo).format("YYYY-MM-DD");
    await downloadFile(
      `/cost-analysis/${current._id}/excel`,
      `cost_analysis_${fromStr}_${toStr}.xlsx`,
      {},
      "arraybuffer"
    );
  };

  const list: ICostAnalysisListItem[] = Array.isArray(listData) ? listData : [];

  const columns = [
    { title: "Продукт", dataIndex: "produceName", key: "produceName" },
    { title: "Количество", dataIndex: "qty", key: "qty", width: 120 },
    {
      title: "Доля (%)",
      key: "share",
      width: 100,
      render: (_: unknown, row: ICostAnalysis["items"][0]) => ((row.share || 0) * 100).toFixed(2),
    },
    {
      title: "Распределённые расходы (TJS)",
      dataIndex: "allocatedExpense",
      key: "allocatedExpense",
      width: 180,
      render: (v: number) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Себестоимость за ед. (распред., TJS)",
      dataIndex: "unitCost",
      key: "unitCost",
      width: 200,
      render: (v: number) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Прямая себестоимость за ед. (TJS)",
      dataIndex: "primeCostPerUnitTJS",
      key: "primeCostPerUnitTJS",
      width: 220,
      render: (v: number) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Чистая себестоимость за ед. (TJS)",
      dataIndex: "netCostPerUnit",
      key: "netCostPerUnit",
      width: 220,
      render: (v: number) => (v != null ? Number(v).toFixed(2) : "-"),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Анализ себестоимости</Title>

      <Card title="Новый расчёт (только за месяц)" style={{ marginBottom: 24 }}>
        <Space wrap align="center">
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(date) => date && setSelectedMonth(date.startOf("month"))}
            format="MMMM YYYY"
            placeholder="Выберите месяц"
          />
          <Button type="primary" onClick={handleCreate} loading={creating}>
            Создать
          </Button>
        </Space>
      </Card>

      {current && (
        <Card
          title={`Результат: ${dayjs(current.periodFrom).format("DD.MM.YYYY")} — ${dayjs(current.periodTo).format("DD.MM.YYYY")}`}
          extra={
            <Button type="primary" onClick={handleDownloadExcel} loading={downloadLoading}>
              Скачать Excel
            </Button>
          }
        >
          <Space size="large" style={{ marginBottom: 16 }} wrap>
            <Statistic
              title="Общая сумма расходов (TJS)"
              value={current.totalExpenses}
              precision={2}
            />
            {current.totalSalaryPricingTJS != null && current.totalSalaryPricingTJS > 0 && (
              <Statistic
                title="Зарплаты (итог), TJS"
                value={current.totalSalaryPricingTJS}
                precision={2}
              />
            )}
            <Statistic title="Общее количество производства" value={current.totalProductionQty} />
            <Statistic title="Создал" value={current.createdByName} />
          </Space>
          <Table
            rowKey={(r) => r.produceId + String(r.qty)}
            columns={columns}
            dataSource={current.items || []}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {list.length > 0 && !current && (
        <Card title="Последние расчёты">
          <Table<ICostAnalysisListItem>
            rowKey="_id"
            dataSource={list}
            pagination={{ pageSize: 10 }}
            columns={[
              {
                title: "Период",
                key: "period",
                render: (_: unknown, row: ICostAnalysisListItem) =>
                  `${dayjs(row.periodFrom).format("DD.MM.YYYY")} — ${dayjs(row.periodTo).format("DD.MM.YYYY")}`,
              },
              { title: "Расходы (TJS)", dataIndex: "totalExpenses", key: "totalExpenses", render: (v: number) => v?.toFixed(2) },
              { title: "Производство", dataIndex: "totalProductionQty", key: "totalProductionQty" },
              { title: "Создал", dataIndex: "createdByName", key: "createdByName" },
              {
                title: "Действия",
                key: "actions",
                render: (_: unknown, row: ICostAnalysisListItem) => (
                  <Button type="link" onClick={() => navigate(`/cost-analysis/${row._id}`)}>
                    Открыть
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
